import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sprite, SparkleBurst, PromptCapsule } from "./fx";
import { SpeakerIcon } from "../ui-icons";

// Tiptoe — tiptoe across on footsteps; tapping too fast makes noise. Keep a
// calm pace so the readers stay peaceful. Faithful port of iOS
// CityHelperTiptoeView.

const STEP_POINTS = [
  { x: 0.18, y: 0.82 },
  { x: 0.34, y: 0.68 },
  { x: 0.5, y: 0.56 },
  { x: 0.66, y: 0.46 },
  { x: 0.82, y: 0.36 },
];

const Tiptoe = ({ payload, sounds, onDone }) => {
  const total = Math.min(payload.steps || 5, STEP_POINTS.length);
  const doneRef = useRef(false);
  const [step, setStep] = useState(0);
  const [ready, setReady] = useState(true);
  const [noise, setNoise] = useState(0);
  const [shh, setShh] = useState(0);
  const [readersWiggle, setReadersWiggle] = useState(0);
  const [done, setDone] = useState(false);
  const [sparkle, setSparkle] = useState(0);
  const noiseRef = useRef(0);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (noiseRef.current > 0) {
        noiseRef.current = Math.max(0, noiseRef.current - 0.05);
        setNoise(noiseRef.current);
      }
    }, 100);
    return () => window.clearInterval(id);
  }, []);

  const finish = useCallback(() => {
    setDone(true);
    sounds.playPop(0.7);
    sounds.playSparkle();
    setSparkle((s) => s + 1);
    if (!doneRef.current) {
      doneRef.current = true;
      window.setTimeout(() => onDone(), 1300);
    }
  }, [onDone, sounds]);

  const tap = useCallback(
    (i) => {
      if (i !== step || done) return;
      if (!ready) {
        noiseRef.current = Math.min(1, noiseRef.current + 0.45);
        setNoise(noiseRef.current);
        setShh((s) => s + 1);
        sounds.playThud();
        setReadersWiggle((w) => w + 1);
        if (noiseRef.current >= 1) sounds.speak("Shh… slow and quiet.");
        return;
      }
      sounds.playPop(0.4);
      const next = step + 1;
      setStep(next);
      setReady(false);
      if (next >= total) {
        finish();
      } else {
        window.setTimeout(() => setReady(true), 550);
      }
    },
    [done, finish, ready, sounds, step, total]
  );

  return (
    <div className="ch-activity-body ch-tiptoe">
      <PromptCapsule text={payload.prompt} sounds={sounds} />

      <div className="ch-tiptoe-meter">
        <span className="ch-tiptoe-meter-icon" aria-hidden="true"><SpeakerIcon size={20} /></span>
        <div className="ch-tiptoe-meter-track">
          <div className="ch-tiptoe-meter-fill" style={{ width: `${Math.max(noise * 100, 4)}%` }} />
        </div>
        <span className="ch-tiptoe-meter-label">Quiet</span>
      </div>

      <div className="ch-stage">
        <motion.div
          className="ch-tiptoe-readers"
          style={{ left: "55%", top: "16%" }}
          animate={readersWiggle ? { x: [0, -5, 5, -3, 3, 0] } : {}}
          transition={{ duration: 0.4 }}
          key={readersWiggle}
        >
          {payload.readers.map((asset, i) => (
            <span key={i} className="ch-tiptoe-reader">
              <span className="ch-tiptoe-reader-shadow" />
              <Sprite name={asset} />
            </span>
          ))}
        </motion.div>

        <AnimatePresence>
          {shh > 0 && (
            <motion.span
              key={shh}
              className="ch-tiptoe-shh"
              style={{ left: "55%", top: "28%" }}
              initial={{ opacity: 1, scale: 0.8, y: 0 }}
              animate={{ opacity: 0, scale: 1.3, y: -30 }}
              transition={{ duration: 0.8 }}
            >
              shh!
            </motion.span>
          )}
        </AnimatePresence>

        {STEP_POINTS.slice(0, total).map((p, i) => {
          const isDone = i < step;
          const isCurrent = i === step && ready && !done;
          return (
            <button
              key={i}
              type="button"
              className={`ch-tiptoe-step ${isDone ? "is-done" : ""} ${isCurrent ? "is-current" : ""}`}
              style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%` }}
              onClick={() => tap(i)}
              aria-label={isCurrent ? "Tiptoe here" : `Step ${i + 1}`}
            >
              {isCurrent && <span className="ch-tiptoe-glow" />}
              <motion.span
                style={{ display: "grid", placeItems: "center" }}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 260, damping: 15, delay: 0.15 + i * 0.09 }}
              >
                <Sprite name={payload.stepAsset || "ch_footprints"} />
              </motion.span>
            </button>
          );
        })}

        {done && (
          <motion.span
            className="ch-tiptoe-win"
            style={{ left: "55%", top: "42%" }}
            initial={{ scale: 0.4, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
          >
            Tiptoed past!
          </motion.span>
        )}

        <SparkleBurst trigger={sparkle} reach={90} style={{ left: "82%", top: "36%" }} />
      </div>
    </div>
  );
};

export default Tiptoe;
